import { useState, useEffect, useContext } from "react";
import { TripFiltersContext } from "../../context/trip-filters.context";
import { CompareContext } from "../../context/compare.context";
import { getQuotationsByFilter } from "../../service/quotation.service";
import BasicTripSearcher from "../../components/basic-trip-searcher/basic-trip-searcher.component";
import FilteredResultList from "../../components/filtered-result-list/filtered-result-list.component";
import TrustBar from "../../components/trust-bar/trust-bar.component";
import { Box, Container } from "@mui/material";

// Seguradoras consultadas, cada uma em paralelo (mostra conforme chega).
const COMPANIES = ["affinity", "universal-assistance", "assist-card"];

// Guarda o resultado da última busca completa. Assim, ao voltar da tela de
// comparação (ou do checkout), os planos aparecem na hora, sem cotar de novo.
// Só guarda se todas as seguradoras responderam, e por no máximo 15 minutos
// (depois disso os preços podem ter mudado).
const CACHE_TTL_MS = 15 * 60 * 1000;
let lastSearch = { key: null, results: null, savedAt: 0 };
// Última busca pedida (mesmo que não tenha terminado). Usada para só limpar a
// comparação quando a pessoa realmente muda a viagem.
let lastRequestedKey = null;

const SearchResults = () => {
  const [resultList, setResultList] = useState(null);
  const [loading, setLoading] = useState(resultList == null);
  // "searching" = ainda há seguradora respondendo (mostra "buscando mais ofertas...")
  const [searching, setSearching] = useState(false);
  // Situação de cada seguradora na busca atual, para a tela de progresso:
  // { affinity: { status: 'pending' | 'done' | 'failed', count } }
  const [companyStatus, setCompanyStatus] = useState({});
  const {
    destination,
    boardingDate,
    arrivalDate,
    youngPassengers,
    oldPassengers,
  } = useContext(TripFiltersContext);
  const { clearCompare } = useContext(CompareContext);

  useEffect(() => {
    if (
      destination &&
      boardingDate &&
      arrivalDate &&
      (youngPassengers || oldPassengers)
    ) {
      const filter = {
        destination,
        boardingDate,
        arrivalDate,
        youngPassengers,
        oldPassengers,
      };

      const searchKey = JSON.stringify(filter);
      if (
        lastSearch.key === searchKey &&
        Date.now() - lastSearch.savedAt < CACHE_TTL_MS
      ) {
        setResultList(lastSearch.results);
        setLoading(false);
        setSearching(false);
        return;
      }

      // Viagem diferente: os preços mudam, então a comparação anterior perde o sentido.
      if (searchKey !== lastRequestedKey) clearCompare();
      lastRequestedKey = searchKey;
      setLoading(true);
      setSearching(true);
      setResultList([]);
      setCompanyStatus(
        Object.fromEntries(COMPANIES.map((c) => [c, { status: "pending", count: 0 }]))
      );

      // Evita que respostas de uma busca antiga sobrescrevam uma busca nova
      // (ex: o usuário muda os filtros antes de tudo terminar).
      let cancelled = false;
      let accumulated = [];
      let pending = COMPANIES.length;
      let hadFailure = false;

      COMPANIES.forEach((company) => {
        getQuotationsByFilter(filter, company).then((data) => {
          if (cancelled) return;
          if (data === null) hadFailure = true;
          setCompanyStatus((current) => ({
            ...current,
            [company]: {
              status: data === null ? "failed" : "done",
              count: data?.length ?? 0,
            },
          }));

          accumulated = [...accumulated, ...(data || [])].sort(
            (a, b) => a.passengerPrice - b.passengerPrice
          );
          setResultList(accumulated);

          // Assim que houver qualquer resultado, já mostra (tira o loader cheio).
          if (accumulated.length > 0) setLoading(false);

          pending -= 1;
          if (pending === 0) {
            setLoading(false);
            setSearching(false);
            if (!hadFailure) {
              lastSearch = { key: searchKey, results: accumulated, savedAt: Date.now() };
            }
          }
        });
      });

      return () => {
        cancelled = true;
      };
    } else {
      setResultList([]);
      setLoading(false);
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, boardingDate, arrivalDate, youngPassengers, oldPassengers]);

  return (
    <Container sx={{ margin: "2rem auto" }}>
      <BasicTripSearcher />
      <Box sx={{ mt: 2 }}>
        <TrustBar inline />
      </Box>
      <FilteredResultList
        resultList={resultList}
        loading={loading}
        searching={searching}
        companyStatus={companyStatus}
      />
    </Container>
  );
};

export default SearchResults;
